import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { NAV_LINKS } from '@/data/site';

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState('');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const current = new URLSearchParams(location.search).get('q');
    setKeyword(current || '');
  }, [location.search]);

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    navigate(trimmed ? `/search?q=${encodeURIComponent(trimmed)}` : '/search');
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? 'bg-primary-950/98 backdrop-blur-md' : 'bg-primary-950'
      }`}
    >
      <div className="flex h-[68px] w-full items-center gap-6 px-4 md:px-8">
        <Link to="/" className="group flex shrink-0 cursor-pointer items-center gap-2.5" aria-label="大雷影视首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-background-50 transition-transform group-hover:-rotate-6">
            <i className="ri-flashlight-fill text-xl" />
          </span>
          <span className="flex items-baseline gap-1.5">
            <span className="text-xl font-black tracking-tight text-background-50">大雷</span>
            <span className="hidden text-[11px] tracking-[0.28em] text-background-300/70 sm:inline">影视大全</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {NAV_LINKS.map((link) => {
            const active = link.path === '/' ? location.pathname === '/' : location.pathname.startsWith(link.path.split('/').slice(0, 2).join('/'));
            return (
              <NavLink
                key={link.path}
                to={link.path}
                className={`relative cursor-pointer whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                  active ? 'text-background-50' : 'text-background-200/80 hover:text-background-50'
                }`}
              >
                {link.label}
                {active && <span className="absolute inset-x-3 -bottom-0.5 h-0.5 rounded-full bg-accent-500" />}
              </NavLink>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <form onSubmit={handleSearch} className="hidden items-center lg:flex" role="search">
            <div className="flex h-9 w-[240px] items-center gap-2 rounded-full border border-background-50/15 bg-background-50/10 pl-3.5 pr-1 transition-colors focus-within:border-accent-500/60 focus-within:bg-background-50/15">
              <i className="ri-search-line text-sm text-background-300/80" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="搜索影视剧、演员"
                aria-label="搜索影视作品"
                className="w-full bg-transparent text-sm text-background-50 outline-none placeholder:text-background-300/60"
              />
              <button
                type="submit"
                className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full bg-accent-500 text-background-50 transition-colors hover:bg-accent-600"
                aria-label="提交搜索"
              >
                <i className="ri-arrow-right-line text-sm" />
              </button>
            </div>
          </form>

          <Link
            to="/search"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-background-50/10 text-background-100 transition-colors hover:bg-background-50/20 lg:hidden"
            aria-label="搜索影视作品"
          >
            <i className="ri-search-line text-lg" />
          </Link>
        </div>
      </div>
    </header>
  );
}